import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, PackagePlus, Share2, Trash2 } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { ddmmyyyy, inr, num, todayIST } from "../../lib/format";
import { whatsappLink } from "../../lib/whatsapp";
import { useAuth } from "../../context/AuthContext";
import { ConfirmDialog } from "../setup/ConfirmDialog";
import { ExportButton } from "../ExportButton";

export function dailyMessage(d, company = "Man & Moda Garments") {
  const lines = [`*DAILY SHIRT STOCK* · ${company}`, `Date: ${ddmmyyyy(d.date)}`, "",
    `From fabricators: *${num(d.totals.from_fabricators)}*`, `Pants purchased: *${num(d.totals.from_purchase)}*`, `Transferred: *${num(d.totals.transferred)}*`, `Sold: *${num(d.totals.sold)}*${d.totals.sold_value ? ` (${inr(d.totals.sold_value)})` : ""}`];
  d.locations.forEach((l) => {
    lines.push("", `*${l.location}*${l.kind ? ` (${l.kind})` : ""}`,
      `Received ${num(l.received)} · Sent ${num(l.sent)} · Sold ${num(l.sold)}${l.sold_value ? ` (${inr(l.sold_value)})` : ""}${d.date === todayIST() ? ` · Stock now ${num(l.in_stock_now)}` : ""}`);
    l.items.forEach((it) => lines.push(`- ${it.description}${it.style ? ` (${it.style})` : ""}: +${num(it.received)} / -${num(it.sent)} / sold ${num(it.sold)}`));
  });
  return lines.join("\n");
}

const SalesList = ({ date, locations, onChanged }) => {
  const { isAdmin } = useAuth();
  const [loc, setLoc] = useState("");
  const [list, setList] = useState(null);
  const [del, setDel] = useState(null);
  const load = () => api.get("/sales", { params: { date, location: loc } }).then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
  }, [date, loc]); // eslint-disable-line react-hooks/exhaustive-deps
  const remove = async () => {
    const s = del;
    setDel(null);
    try {
      await api.delete(`/sales/${s.id}`);
      toast.success(`${s.sale_no} deleted, stock restored`);
      load();
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3" data-testid="sales-register">
      <div className="flex flex-wrap items-center justify-between gap-2 px-1">
        <h2 className="mono-label text-muted-foreground">Sales on {ddmmyyyy(date)}</h2>
        <ExportButton name="sales" label="All sales" />
        <select className="field" value={loc} onChange={(e) => setLoc(e.target.value)} data-testid="sales-location-filter">
          <option value="">All locations</option>
          {locations.map((l) => <option key={l.location} value={l.location}>{l.location}</option>)}
        </select>
      </div>
      {!list ? <p className="text-muted-foreground">Loading…</p>
        : !list.length ? <p className="panel p-4 text-center text-sm text-muted-foreground" data-testid="sales-empty">No sales recorded.</p>
        : list.map((s) => (
          <div key={s.id} className="panel space-y-1 p-4" data-testid={`sale-${s.sale_no}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="mono font-bold">{s.sale_no}</span>
              <span className="mono text-right font-bold text-brand" data-testid={`sale-${s.sale_no}-qty`}>{num(s.qty_total)} pcs
                {!!s.value_total && <span className="block text-xs" data-testid={`sale-${s.sale_no}-value`}>{inr(s.value_total)}</span>}</span>
            </div>
            <p className="text-sm font-semibold">{s.location}{s.customer_name ? ` · ${s.customer_name}` : ""}{s.customer_mobile ? ` (${s.customer_mobile})` : ""}</p>
            <p className="text-xs text-muted-foreground">{s.items.map((i) => `${i.description}${i.style ? ` (${i.style})` : ""} × ${num(i.qty)}${i.rate ? ` @ ${inr(i.rate)}` : ""}`).join(", ")}</p>
            {s.remarks && <p className="text-xs text-muted-foreground">{s.remarks}</p>}
            {isAdmin && <button className="btn btn-outline btn-ghost-danger mt-2 w-full" onClick={() => setDel(s)} data-testid={`sale-${s.sale_no}-delete-btn`}><Trash2 size={16} /> Delete</button>}
          </div>
        ))}
      <ConfirmDialog open={!!del} onCancel={() => setDel(null)} onConfirm={remove} title={`Delete ${del?.sale_no}?`} body="The shirts go back into that location's stock." />
    </div>
  );
};

export const RefillSuggestions = ({ reloadKey }) => {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/refill-suggestions").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [reloadKey]);
  if (!data || (!data.suggestions.length && !data.unfilled.length)) return null;
  return (
    <div className="rise space-y-3 rounded-[14px] border border-brand/40 bg-brand/5 p-4" data-testid="refill-suggestions">
      <p className="flex items-center gap-2 font-heading font-bold"><PackagePlus size={18} /> Suggested refills</p>
      <p className="text-xs text-muted-foreground">Shirts to send from a godown to bring each showroom back to its minimum. Shirts already on the way are counted.</p>
      {data.suggestions.map((s, i) => (
        <div key={i} className="panel space-y-2 p-3" data-testid={`refill-${i}`}>
          <p className="flex items-center gap-1 text-sm font-semibold">{s.from_location} <ArrowRight size={14} /> {s.to_location}
            <span className="mono ml-auto text-brand">{num(s.qty_total)}</span></p>
          <p className="text-xs text-muted-foreground">{s.items.map((it) => `${it.description}${it.style ? ` (${it.style})` : ""} × ${num(it.qty)}`).join(", ")}</p>
          {can("stock_write") && <Link to="/challans/new" state={{ prefill: s }} className="btn btn-accent w-full" data-testid={`refill-${i}-create-btn`}>Create challan</Link>}
        </div>
      ))}
      {!!data.unfilled.length && (
        <p className="text-xs text-destructive" data-testid="refill-unfilled">Not enough in godowns for: {data.unfilled.map((u) => `${u.description}${u.style ? ` (${u.style})` : ""} at ${u.location}, need ${num(u.short_by)}`).join("; ")}</p>
      )}
    </div>
  );
};

const Stat = ({ label, value, tone = "", testid }) => (
  <div className="flex flex-col justify-end text-center">
    <p className="mono-label text-[10px] text-muted-foreground">{label}</p>
    <p className={`mono text-lg font-bold ${tone}`} data-testid={testid}>{num(value)}</p>
  </div>
);

const LocationDay = ({ l, isToday }) => (
  <div className="panel space-y-2 p-4" data-testid={`daily-loc-${l.location}`}>
    <p className="font-heading font-bold">{l.location}{l.kind && <span className="ml-2 text-xs font-normal text-muted-foreground">{l.kind}</span>}</p>
    <div className="grid grid-cols-4 gap-1">
      <Stat label="Received" value={l.received} tone="text-brand" testid={`daily-loc-${l.location}-received`} />
      <Stat label="Sent" value={l.sent} testid={`daily-loc-${l.location}-sent`} />
      <Stat label="Sold" value={l.sold} testid={`daily-loc-${l.location}-sold`} />
      {isToday ? <Stat label="Stock now" value={l.in_stock_now} testid={`daily-loc-${l.location}-stock`} /> : <span />}
    </div>
    {!!l.sold_value && <p className="text-right text-xs" data-testid={`daily-loc-${l.location}-sales-value`}>Sales value <b className="mono">{inr(l.sold_value)}</b></p>}
    {!!l.items.length && (
      <div className="divide-y text-xs">
        {l.items.map((it) => (
          <div key={it.key} className="flex justify-between gap-2 py-1.5">
            <span className="min-w-0 truncate">{it.description}{it.style ? ` · ${it.style}` : ""}</span>
            <span className="mono shrink-0 text-muted-foreground">+{num(it.received)} / −{num(it.sent)} / sold {num(it.sold)}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const DailySummary = () => {
  const { can } = useAuth();
  const [date, setDate] = useState(todayIST());
  const [data, setData] = useState(null);
  const [company, setCompany] = useState(undefined);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    api.get("/settings").then((r) => setCompany(r.data.company_name)).catch(() => {});
  }, []);
  useEffect(() => {
    if (date) api.get("/daily-stock", { params: { date } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [date, tick]);
  return (
    <div className="space-y-4" data-testid="daily-summary">
      <div className="panel flex items-center gap-3 p-4">
        <input type="date" className="field" value={date} onChange={(e) => setDate(e.target.value)} data-testid="daily-date-input" />
        {can("stock_write") && <Link to="/sales/new" className="btn btn-primary shrink-0" data-testid="daily-record-sale-btn">Record sale</Link>}
      </div>
      {!data ? <p className="text-muted-foreground">Loading…</p> : (
        <>
          <div className="panel p-4" data-testid="daily-totals">
            <p className="mb-2 font-heading font-bold">All locations · {ddmmyyyy(data.date)}</p>
            <div className="mb-3 grid grid-cols-4 gap-1">
              <Stat label="From fabricators" value={data.totals.from_fabricators} tone="text-brand" testid="daily-total-received" />
              <Stat label="Pants purchased" value={data.totals.from_purchase} tone="text-brand" testid="daily-total-purchase" />
              <Stat label="Transferred" value={data.totals.transferred} testid="daily-total-sent" />
              <Stat label="Sold" value={data.totals.sold} testid="daily-total-sold" />
            </div>
            {!!data.totals.sold_value && <p className="mb-3 text-center text-sm" data-testid="daily-total-sales-value">Sales value <b className="mono">{inr(data.totals.sold_value)}</b></p>}
            <a href={whatsappLink(dailyMessage(data, company || undefined))} target="_blank" rel="noreferrer" className="btn btn-accent w-full" data-testid="daily-share-whatsapp-btn"><Share2 size={18} /> Share on WhatsApp</a>
          </div>
          {data.locations.map((l) => <LocationDay key={l.location} l={l} isToday={date === todayIST()} />)}
          <p className="px-1 text-xs text-muted-foreground">At each location, Received = shirts from fabricators plus challans received that day; Sent = challans dispatched that day. Moves between your own locations are not counted as new shirts in the top total.</p>
          <SalesList date={data.date} locations={data.locations} onChanged={() => setTick((t) => t + 1)} />
        </>
      )}
    </div>
  );
};
